import { useMemo, useState } from 'react'
import { Box } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'

import { CHART_SERIES } from '@/components/@Shared/AssetsDebtChart/AssetsDebtChart.constants'
import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { AreaChart } from '@/components/AreaChart/AreaChart'
import { Loader } from '@/components/Loader/Loader'
import { Tabs } from '@/components/Tabs/Tabs'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import {
  AdvisorsService,
  type BankingControllerGetLinearStatsData,
  BankingService,
} from '@/__generated__/requests'

export function AssetsDebtChart({ userId }: { userId?: number }) {
  const [activeTab, setActiveTab] = useState<string>('net-worth')
  const [period, setPeriod] =
    useState<BankingControllerGetLinearStatsData['period']>('year')
  const [type, setType] =
    useState<BankingControllerGetLinearStatsData['type']>('net-worth')

  const isUser = !!userId

  const queryFn = isUser
    ? () =>
        AdvisorsService.advisorUsersControllerGetLinearStats({
          period,
          type,
          userId: +userId,
        })
    : () =>
        BankingService.bankingControllerGetLinearStats({
          period,
          type,
        })

  const queryKey = isUser
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetLinearStatsKeyFn({
        period,
        type,
        userId: +userId,
      })
    : Common.UseBankingServiceBankingControllerGetLinearStatsKeyFn({
        period,
        type,
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
  })

  const chartData = useMemo(() => data ?? [], [data])

  const tabChild = chartData.length ? (
    <AreaChart data={chartData} dataKey="date" series={CHART_SERIES} />
  ) : (
    <Box
      style={{
        textAlign: 'center',
      }}
    >
      <Title mb={4} variant="h2">
        No accounts added
      </Title>
      <Text variant="body_1">Add debt account first to see the chart.</Text>
    </Box>
  )

  const TABS = [
    {
      children: tabChild,
      id: 'net-worth',
      label: 'Net Worth',
      onClick: () => {
        setType('net-worth')
        setActiveTab('net-worth')
      },
    },
    {
      children: tabChild,
      id: 'assets',
      label: 'Assets',
      onClick: () => {
        setType('assets')
        setActiveTab('assets')
      },
    },
    {
      children: tabChild,
      id: 'debt',
      label: 'Debt',
      onClick: () => {
        setType('debt')
        setActiveTab('debt')
      },
    },
  ]

  const FILTERS = [
    {
      label: '1M',
      onClick: () => setPeriod('month'),
      value: 'month',
    },
    {
      label: '3M',
      onClick: () => setPeriod('quarter'),
      value: 'quarter',
    },
    {
      label: '6M',
      onClick: () => setPeriod('half-year'),
      value: 'half-year',
    },
    {
      label: '1Y',
      onClick: () => setPeriod('year'),
      value: 'year',
    },
  ]

  return (
    <ShadowBox>
      {isFetching || isLoading ? (
        <Loader isMinimized />
      ) : (
        <Tabs
          activeFilter={period}
          activeTab={activeTab}
          filters={FILTERS}
          tabs={TABS}
        />
      )}
    </ShadowBox>
  )
}
